import { useEffect } from "react"
import { useAnimate, useReducedMotion } from "framer-motion"
import { theme } from "../settings/config"
import CoachAvatar from "./CoachAvatar"

// ──────────────────────────────────────────────────────────────────────────
// LE ICONE DELLA BARRA IN BASSO, CHE SI MUOVONO
//
// Sono le tre icone di FontAwesome della barra (allievi, la bacchetta, il
// libro) ridisegnate a pezzi: quelle di FontAwesome sono un tracciato unico,
// e le stelline non si staccano dalla bacchetta. A riposo
// sono quasi le stesse; toccate fanno ognuna la sua mossa, una volta:
//
// - Allievi: quello davanti fa un saltello, quelli di lato gli si stringono
// - Lezioni: la bacchetta dà un colpo, le stelline si accendono una per una
// - Quaderno: la copertina si apre sulle pagine e si richiude
//
// e sopra, tutte, un rimbalzo. `play` è un contatore: ogni volta che cresce
// la mossa riparte, anche toccando la voce in cui si è già. A 0 sta ferma:
// aprendo l'app non si muove niente. Con "riduci movimento" acceso nel
// telefono le icone restano ferme sempre.
//
// Il profilo sta a parte, nella sua isola tonda, ed è la propria foto
// (`ProfileFace`, qui in fondo): rimbalza come le altre, senza una mossa sua.
// ──────────────────────────────────────────────────────────────────────────

// la molla che passa un po' oltre e torna: quella del rimbalzo
const OVERSHOOT = [0.34, 1.56, 0.64, 1]

// i pezzi che girano o si allargano attorno al proprio centro
const AROUND_ITSELF = { transformBox: "fill-box", transformOrigin: "center" }

// I pezzi dentro l'icona si muovono con le animazioni del browser (Web
// Animations), non con `animate` di framer-motion: sugli elementi dentro un
// SVG framer-motion calcola la mossa ma non la applica (le manca la misura
// dell'elemento, che prende solo per i suoi componenti), e i pezzi restavano
// fermi: si vedeva solo il rimbalzo. Le mosse sono scritte allo stesso modo,
// { x, y, rotate, scale, scaleX, opacity } con i loro fotogrammi.
const EASINGS = { easeInOut: "ease-in-out", easeOut: "ease-out", easeIn: "ease-in" }
const easing = ease =>
	Array.isArray(ease) ? `cubic-bezier(${ease.join(", ")})` : EASINGS[ease] ?? "ease"

const frameAt = (values, i) => {
	const at = key => {
		const frames = values[key]
		if (frames === undefined) return undefined
		return frames[Math.min(i, frames.length - 1)]
	}
	const [x, y, rotate, scale, scaleX, opacity] = ["x", "y", "rotate", "scale", "scaleX", "opacity"].map(at)
	const transform = [
		(x !== undefined || y !== undefined) && `translate(${x ?? 0}px, ${y ?? 0}px)`,
		rotate !== undefined && `rotate(${rotate}deg)`,
		scale !== undefined && `scale(${scale})`,
		scaleX !== undefined && `scaleX(${scaleX})`,
	]
		.filter(Boolean)
		.join(" ")
	return { ...(transform && { transform }), ...(opacity !== undefined && { opacity }) }
}

const movePart = scope => (selector, values, { duration, delay = 0, ease } = {}) => {
	const count = Math.max(...Object.values(values).map(frames => frames.length))
	const frames = Array.from({ length: count }, (_, i) => frameAt(values, i))
	scope.current?.querySelectorAll(selector).forEach(el =>
		el.animate?.(frames, {
			duration: duration * 1000,
			delay: delay * 1000,
			easing: easing(ease),
			// durante l'attesa il pezzo sta già al primo fotogramma (le stelline
			// spente), e alla fine torna com'era
			fill: "backwards",
		}),
	)
}

function useMove(play, move) {
	const [scope, animate] = useAnimate()
	const reduceMotion = useReducedMotion()

	useEffect(() => {
		if (!play || reduceMotion || !scope.current) return
		animate(scope.current, { scale: [0.8, 1.14, 1] }, { duration: 0.45, ease: OVERSHOOT })
		move(movePart(scope))
		// la mossa è fissa: conta solo quando cambia `play`
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [play])

	return scope
}

const svgProps = (color, viewBox) => ({
	viewBox,
	// grandi quanto i bottoni tondi che stanno sopra la barra: a 22×20
	// sembravano piccole accanto a tutto il resto
	width: 28,
	height: 25.5,
	"aria-hidden": true,
	style: { color, overflow: "visible", display: "block" },
})

export function PlayersGlyph({ color, play }) {
	const scope = useMove(play, animate => {
		animate(".mid", { y: [0, -3, 0] }, { duration: 0.55, ease: OVERSHOOT })
		animate(".left", { x: [0, 1.6, 0] }, { duration: 0.55, ease: "easeInOut" })
		animate(".right", { x: [0, -1.6, 0] }, { duration: 0.55, ease: "easeInOut" })
	})

	return (
		<svg ref={scope} {...svgProps(color, "0 0 24 22")}>
			<g className="left" fill="currentColor">
				<circle cx="5" cy="6.2" r="2.7" />
				<path d="M0.6 17.2 a4.4 4.4 0 0 1 6.4 -4.1 a7.2 7.2 0 0 0 -1.4 4.7 v0.3 h-4.2 a0.8 0.8 0 0 1 -0.8 -0.9z" />
			</g>
			<g className="right" fill="currentColor">
				<circle cx="19" cy="6.2" r="2.7" />
				<path d="M23.4 17.2 a4.4 4.4 0 0 0 -6.4 -4.1 a7.2 7.2 0 0 1 1.4 4.7 v0.3 h4.2 a0.8 0.8 0 0 0 0.8 -0.9z" />
			</g>
			<g className="mid" fill="currentColor">
				<circle cx="12" cy="7.6" r="3.5" />
				<path d="M6.6 19.4 a5.4 5.4 0 0 1 10.8 0 v0.4 a0.9 0.9 0 0 1 -0.9 0.9 h-9 a0.9 0.9 0 0 1 -0.9 -0.9z" />
			</g>
		</svg>
	)
}

const SPARKS = [
	{ d: "M18 1.2 Q18 5.6 22.4 5.6 Q18 5.6 18 10 Q18 5.6 13.6 5.6 Q18 5.6 18 1.2Z", delay: 0.18 },
	{ d: "M8.4 1.4 Q8.4 3.9 10.9 3.9 Q8.4 3.9 8.4 6.4 Q8.4 3.9 5.9 3.9 Q8.4 3.9 8.4 1.4Z", delay: 0.28 },
	{
		d: "M20.4 12.2 Q20.4 14.8 23 14.8 Q20.4 14.8 20.4 17.4 Q20.4 14.8 17.8 14.8 Q20.4 14.8 20.4 12.2Z",
		delay: 0.38,
	},
]

export function LessonsGlyph({ color, play }) {
	const scope = useMove(play, animate => {
		animate(".stick", { rotate: [0, -22, 6, 0] }, { duration: 0.6, ease: OVERSHOOT })
		SPARKS.forEach(({ delay }, i) =>
			animate(
				`.spark-${i}`,
				{ scale: [0, 1.35, 1], rotate: [-45, 10, 0], opacity: [0, 1, 1] },
				{ duration: 0.55, delay, ease: OVERSHOOT },
			),
		)
	})

	return (
		<svg ref={scope} {...svgProps(color, "0 0 24 24")}>
			{/* la bacchetta gira attorno al manico, in basso a sinistra */}
			<g
				className="stick"
				stroke="currentColor"
				strokeWidth="3.6"
				strokeLinecap="round"
				style={{ transformBox: "view-box", transformOrigin: "4px 20px" }}>
				<line x1="4" y1="20" x2="13" y2="11" />
				<line x1="13.2" y1="10.8" x2="15.6" y2="8.4" opacity="0.5" />
			</g>
			{SPARKS.map(({ d }, i) => (
				<path key={d} className={`spark-${i}`} d={d} fill="currentColor" style={AROUND_ITSELF} />
			))}
		</svg>
	)
}

export function NotebookGlyph({ color, play }) {
	const scope = useMove(play, animate => {
		animate(".cover", { scaleX: [1, 0.25, 1] }, { duration: 0.6, ease: [0.34, 1.3, 0.55, 1] })
	})

	return (
		<svg ref={scope} {...svgProps(color, "0 0 24 22")}>
			{/* le pagine, che si vedono quando la copertina si apre */}
			<rect x="5.6" y="2" width="13.4" height="15.6" rx="1.6" fill="currentColor" opacity="0.4" />
			<rect x="5.2" y="17.2" width="14.6" height="3.6" rx="1.4" fill="currentColor" opacity="0.55" />
			{/* la copertina, con le due righe dell'etichetta; si apre dal dorso */}
			<path
				className="cover"
				fillRule="evenodd"
				fill="currentColor"
				style={{ transformBox: "fill-box", transformOrigin: "left center" }}
				d="M6.4 1 h11.2 a2.2 2.2 0 0 1 2.2 2.2 v13 a2.2 2.2 0 0 1 -2.2 2.2 h-11.2 a2.2 2.2 0 0 1 -2.2 -2.2 v-13 a2.2 2.2 0 0 1 2.2 -2.2z M8.4 5 h7.2 v1.7 h-7.2z M8.4 8.5 h7.2 v1.7 h-7.2z"
			/>
		</svg>
	)
}

// Il profilo non ha un'icona: è la propria foto, o senza foto l'iniziale del
// nome (`CoachAvatar`). Toccato fa lo stesso rimbalzo delle icone; dove si è
// dentro lo dice un anello arancione.
export function ProfileFace({ coach, active, play, size }) {
	const scope = useMove(play, () => {})

	return (
		<span ref={scope} style={{ display: "flex", borderRadius: "50%" }}>
			<CoachAvatar
				coach={coach}
				size={size}
				initials={1}
				style={{
					boxShadow: active ? `0 0 0 2.5px ${theme.colors.primary}` : "none",
					transition: "box-shadow 0.2s ease-in-out",
				}}
			/>
		</span>
	)
}
